import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { FreelancerInfoCell } from '../../components/FreelancerInfoCell/FreelancerInfoCell';
import { FreelancerLinkCell } from '../../components/FreelancerLinkCell/FreelancerLinkCell';
import { FreelancerRegionCell } from '../../components/FreelancerRegionCell/FreelancerRegionCell';
import {
    FreelancerServicePriceTag,
    FreelancerServicePriceThemes
} from '../../components/FreelancerServicePriceTag/FreelancerServicePriceTag';
import { FreelancersListCellWrapper } from '../../components/FreelancersListCellWrapper/FreelancersListCellWrapper';
import { FreelancersListCellItem } from '../../components/FreelancersListScreen/FreelancersListScreen';
import { RatingStars } from '../../components/RatingStars/RatingStars';
import { Bem } from '../../service/Bem/Bem';
import { translateFreelancerServiceDuration } from '../../service/FreelancerServiceOfferDuration/FreelancerServiceOfferDuration';
import { GdFreelancerSkillOfferDuration } from '../../typings/graphql-grid';
import { GridCellProps, GridColumnDecl } from '../../typings/grid';

import * as keyset from './freelancers-list.i18n';

const i18n = i18nFactory(keyset);
const BLOCK = 'freelancers-list-screen';

export enum FreelancersListColumnKeys {
    FEEDBACKS_COUNT = 'feedbacks-count',
    FREELANCER = 'freelancer',
    REGION = 'region',
    LOCATION = 'location',
    SCORE = 'score',
    SERVICES = 'services'
}

const tableCellEmpty: GridColumnDecl<FreelancersListCellItem> = {
    title: '',
    key: 'empty',
    width: 20,
    isResizable: false,
    fixed: true,
    isSortable: false,
    cellClass: 'freelancer-vertically-centered',
    CellComponent: React.memo(({ rowData: item }: GridCellProps<FreelancersListCellItem>): JSX.Element => <div />)
};

function getTableCellFreelancerInfo(login: string, freelancersCount: number): GridColumnDecl<FreelancersListCellItem> {
    const title = i18n('column-title-freelancers');

    return {
        title: freelancersCount ? `${title} ${freelancersCount}` : title,
        key: FreelancersListColumnKeys.FREELANCER,
        width: 400,
        isResizable: false,
        fixed: true,
        flexGrow: 5,
        cellClass: 'freelancer-vertically-centered',
        CellComponent: React.memo(
            ({ rowData: item }: GridCellProps<FreelancersListCellItem>): JSX.Element => (
                <FreelancerInfoCell
                    isMyFreelancer={item.isCurrentFreelancer}
                    avatarLink={item.avatarLink}
                    avatarResize={180}
                    about={item.about}
                    fullName={item.fullName}
                    status={item.status}
                    firstService={item.services[0]}
                    servicesCount={item.services.length - 1}
                    freelancerLogin={item.login}
                    ulogin={login}
                />
            )
        )
    };
}

const tableCellCity: GridColumnDecl<FreelancersListCellItem> = {
    title: i18n('column-title-city'),
    key: FreelancersListColumnKeys.REGION,
    width: 200,
    isResizable: false,
    fixed: true,
    cellClass: 'freelancer-vertically-centered',
    CellComponent: React.memo(
        ({ rowData: item }: GridCellProps<FreelancersListCellItem>): JSX.Element => (
            <FreelancerRegionCell region={item.region} />
        )
    )
};

const tableCellFreelancerScore: GridColumnDecl<FreelancersListCellItem> = {
    title: i18n('column-title-score'),
    key: FreelancersListColumnKeys.SCORE,
    width: 120,
    isResizable: false,
    fixed: true,
    cellClass: 'freelancer-vertically-centered',
    CellComponent: React.memo(
        ({ rowData: item }: GridCellProps<FreelancersListCellItem>): JSX.Element => (
            <FreelancersListCellWrapper>
                {!item.freelancerScore ? (
                    <div className={Bem.cls(BLOCK, 'no-score')}>{i18n('noScore')}</div>
                ) : (
                    <RatingStars readonly={true} sizeStars={14} value={item.freelancerScore} />
                )}
            </FreelancersListCellWrapper>
        )
    )
};

const tableCellFreelancerServices: GridColumnDecl<FreelancersListCellItem> = {
    title: i18n('column-title-services'),
    key: FreelancersListColumnKeys.SERVICES,
    width: 180,
    isResizable: false,
    align: 'left',
    fixed: true,
    cellClass: 'freelancer-vertically-centered',
    CellComponent: React.memo(
        ({ rowData: item }: GridCellProps<FreelancersListCellItem>): JSX.Element => {
            const comp = (
                <FreelancerServicePriceTag
                    theme={FreelancerServicePriceThemes.SMALL}
                    approximateDuration={translateFreelancerServiceDuration(
                        GdFreelancerSkillOfferDuration[item.approximateDuration]
                    )}
                    approximatePrice={item.approximatePrice}
                    workCurrency={item.workCurrency}
                />
            );
            if (item.approximateDuration !== undefined && item.approximatePrice !== undefined) {
                return comp;
            }

            return <FreelancersListCellWrapper>{comp}</FreelancersListCellWrapper>;
        }
    )
};

const tableCellFreelancerFeedbacksCount: GridColumnDecl<FreelancersListCellItem> = {
    title: i18n('column-title-feedbacks'),
    key: FreelancersListColumnKeys.FEEDBACKS_COUNT,
    width: 100,
    isResizable: false,
    fixed: true,
    cellClass: 'freelancer-vertically-centered',
    CellComponent: React.memo(
        ({ rowData: item }: GridCellProps<FreelancersListCellItem>): JSX.Element => (
            <FreelancersListCellWrapper>
                <div className={Bem.cls('freelancer-feedbacks')}>{item.feedbackCount}</div>
            </FreelancersListCellWrapper>
        )
    )
};

function getTableCellFreelancerCardLink(login: string): GridColumnDecl<FreelancersListCellItem> {
    return {
        title: '',
        key: 'link',
        width: 50,
        isResizable: false,
        fixed: true,
        cellClass: 'freelancer-link',
        isSortable: false,
        CellComponent: React.memo(
            ({ rowData: item }: GridCellProps<FreelancersListCellItem>): JSX.Element => (
                <FreelancersListCellWrapper isLink={true}>
                    <FreelancerLinkCell ulogin={login} freelancerLogin={item.login} />
                </FreelancersListCellWrapper>
            )
        )
    };
}

export function getFreelancersListColumns(
    login: string,
    freelancersCount: number
): Array<GridColumnDecl<FreelancersListCellItem>> {
    return [
        tableCellEmpty,
        getTableCellFreelancerInfo(login, freelancersCount),
        tableCellFreelancerScore,
        tableCellFreelancerFeedbacksCount,
        tableCellCity,
        tableCellFreelancerServices,
        getTableCellFreelancerCardLink(login)
    ];
}
